/**
 * Helpers for assembling the {@link ConfirmDetail} preview shown inside discard
 * dialogs. Kept generic so every page (transactions, accounts, savings,
 * investments, liabilities) builds its card from the same primitives.
 */

import AddRoundedIcon from "@mui/icons-material/AddRounded";

import { formatCurrency } from "../../../utils";
import { ConfirmDetail, ConfirmTone } from "./types";

const SEPARATOR = " · ";

/** Join the non-empty parts of a detail line with the shared middot separator. */
export const joinDetailParts = (
  ...parts: Array<string | undefined | null>
): string => parts.filter((part) => Boolean(part?.trim())).join(SEPARATOR);

/** Count meaningfully-filled values for the draft "N fields filled" summary. */
export const countFilledFields = (values: unknown[]): number =>
  values.filter((value) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return true;
  }).length;

/** "1 field filled" / "3 fields filled". */
export const filledFieldsLabel = (count: number): string => {
  const suffix = count === 1 ? "field" : "fields";
  return `${count} ${suffix} filled`;
};

/**
 * Parse a raw money input and format it, returning `undefined` when the value
 * is missing or not a number (so the amount line is simply omitted). Replaces
 * the repeated `Number.parseFloat` / `Number.isNaN` dance at the call sites.
 */
export const formatOptionalCurrency = (
  value: string | number | undefined | null,
): string | undefined => {
  const amount =
    typeof value === "number" ? value : Number.parseFloat(String(value ?? ""));
  if (Number.isNaN(amount)) {
    return undefined;
  }
  return formatCurrency(amount);
};

/**
 * Build a count-style summary with a leading status dot, e.g.
 * "1 unsaved change will be discarded". Used by edit forms that track dirty
 * fields rather than a single structured record.
 */
export const buildUnsavedChangesDetails = (
  count: number,
  tone: ConfirmTone = "warning",
): ConfirmDetail => {
  const suffix = count === 1 ? "change" : "changes";
  return {
    dot: tone,
    title: `${count} unsaved ${suffix} will be discarded`,
  };
};

interface DraftDetailsArgs {
  /** Noun for the "Draft <noun>" title, e.g. "saving", "liability". */
  noun: string;
  /** Values fed to {@link countFilledFields} for the "N fields filled" summary. */
  fieldValues: unknown[];
  /** Pre-formatted amount appended to the title (omitted when undefined). */
  amount?: string;
  /** Leading subtitle part, typically the draft's name (omitted when empty). */
  primaryLabel?: string;
}

/**
 * Build the draft ("create new") preview: a "+" badge, a "Draft <noun> · <amount>"
 * title and a "<primaryLabel> · N fields filled" subtitle. Shared by every
 * create panel so the draft card stays consistent.
 */
export const buildDraftDetails = ({
  noun,
  fieldValues,
  amount,
  primaryLabel,
}: DraftDetailsArgs): ConfirmDetail => ({
  icon: <AddRoundedIcon />,
  title: joinDetailParts(`Draft ${noun}`, amount),
  subtitle: joinDetailParts(
    primaryLabel,
    filledFieldsLabel(countFilledFields(fieldValues)),
  ),
});
